// @ts-strict-ignore
import { NextRouter, useRouter } from 'next/router';
import { createContext, useContext, useEffect, useMemo, type JSX } from 'react';

import { arrayOrOne, flatten } from 'f';

import useVideoPlayerStore from 'components/video-player-dubbing/store';
import { playlistReferrer } from 'lib/business';
import { useLocalStorage } from 'lib/hooks/useLocalStorage';
import { useLocalstorageState, useSessionstorageState } from 'rooks';

type PanelStyleTokens = {
  zIndex: number;
  headerHeight: number;
};

type TalkPageContext = {
  // Path components
  slug: string;
  tab: string;

  // Query components
  hasLanguage: boolean;
  language: string;
  playlistSlug: string | null;
  autoplay: boolean | null;
  subtitle: string;
  audio: string;
  tooltips: {
    showDubbing: boolean;
    showTranscript: boolean;
    showSubtitle: boolean;
  };

  // Embed properties
  muted?: boolean;
  start?: number;
  showDetails?: boolean;

  // Transcript tokens
  panelStyleTokens: PanelStyleTokens;
} & NextRouter;

const Context = createContext<TalkPageContext>(null);

export function TalkPageContextProvider({ children }): JSX.Element {
  const { autoplayed, reset, dubbingLang, onTooltipOpen } = useVideoPlayerStore(
    state => ({
      onTooltipOpen: state.onTooltipOpen,
      isTooltipOpen: state.isTooltipOpen,
      autoplayed: state.autoplayed,
      reset: state.reset,
      dubbingLang: state.dubbingLang
    })
  );
  const [autoplayContext] = useLocalstorageState('autoplay_state', autoplayed);
  const [isMuted] = useSessionstorageState('muted_state', false);
  const [isLocalDubbingOpen] = useLocalStorage('dubbing-tooltip', null);
  const [isLocalSubtitleOpen] = useLocalStorage('subtitle-tooltip', null);
  const [isLocalTranscriptOpen] = useLocalStorage('transcript-tooltip', null);

  const router = useRouter();
  const {
    slug: pathComponents,
    language: possibleLanguages,
    subtitle: possibleSubtitles,
    referrer,
    autoplay,
    muted: mutedQuery,
    start: startQuery,
    showDetails: showDetailsQuery,
    audio,
    showDubbingTooltip,
    showTranscriptTooltip,
    showSubtitleTooltip
  } = router.query;
  const [slug, tab] = flatten([pathComponents]);
  const subtitle = arrayOrOne(possibleSubtitles);

  // Extract language from path for embed route
  let language;
  if (router.pathname.startsWith('/embed/talks/lang/')) {
    const pathSegments = router.asPath.split('/');
    const langIndex = pathSegments.findIndex(seg => seg === 'lang');
    language = pathSegments[langIndex + 1];
  } else {
    // Use query parameter for normal talks route
    language = arrayOrOne(possibleLanguages);
  }

  const panelStyleTokens = {
    zIndex: 1000,
    headerHeight: 40
  };

  const value = useMemo(
    () =>
      ({
        ...router,
        slug,
        tab,
        subtitle,
        audio: audio || dubbingLang,
        language: language || 'en',
        hasLanguage: Boolean(language),
        playlistSlug: referrer ? playlistReferrer(referrer) : null,
        autoplay: autoplay === 'true' || Boolean(autoplayContext),
        muted: mutedQuery === 'true' || isMuted,
        start: Number(startQuery),
        showDetails: Boolean(showDetailsQuery),

        tooltips: {
          showDubbing: Boolean(showDubbingTooltip),
          showTranscript: Boolean(showTranscriptTooltip),
          showSubtitle: Boolean(showSubtitleTooltip)
        }
      }) as TalkPageContext,
    [
      router,
      slug,
      tab,
      subtitle,
      audio,
      dubbingLang,
      language,
      referrer,
      autoplay,
      autoplayContext,
      mutedQuery,
      isMuted,
      startQuery,
      showDetailsQuery,
      showDubbingTooltip,
      showTranscriptTooltip,
      showSubtitleTooltip
    ]
  );

  useEffect(() => {
    const {
      tooltips: { showDubbing, showSubtitle, showTranscript }
    } = value;
    if (
      (showDubbing && !isLocalDubbingOpen) ||
      (showSubtitle && !isLocalSubtitleOpen) ||
      (showTranscript && !isLocalTranscriptOpen)
    )
      onTooltipOpen(true);
  }, [
    value,
    onTooltipOpen,
    isLocalDubbingOpen,
    isLocalSubtitleOpen,
    isLocalTranscriptOpen
  ]);

  useEffect(() => {
    // Reset the video player state when navigating to a different talk
    reset('nextTalk');
  }, [slug]);

  return (
    <Context
      value={{
        ...value,
        panelStyleTokens
      }}
    >
      {children}
    </Context>
  );
}

export function useTalkPageContext(): TalkPageContext {
  return useContext(Context);
}
